import React, { useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Form,
  Button,
  Modal,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import { jwtDecode } from "jwt-decode";
import Select from "react-select";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import FocusError from "../../../components/error/error-focus";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import InputField from "../../../components/input/input-field";
import CustomPhoneInput from "../../../components/input/phone-input";
import FadeLoaderComponent from "../../../components/loader-component";
import { roleTypes } from "../../../data/roleType";
import * as SettingSelector from "../../../store/setting/selectors";
import { useSelector } from "react-redux";

const StakeholderEdit = () => {
  const alertRef = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const [errors, setErrors] = useState({});
  const [decodedToken, setDecodedToken] = useState();
  const [resetKey, setResetKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stakeholderData, setStakeholderData] = useState(null);
  const token = localStorage.getItem("coyote-token");
  const permissions = useSelector(SettingSelector.set_permissions);

  const stakeholderHasDeletePermission =
    permissions?.stakeholder?.delete_contact === true;

  const stakeholderHasUpdatePermission =
    permissions?.stakeholder?.update === true;

  const [showPopover, setShowPopover] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  useEffect(() => {
    const fetchStakeholderData = async () => {
      if (!id || !token) return;

      setLoading(true);
      try {
        const config = {
          headers: { Authorization: `${token}` },
        };

        const response = await axios.get(
          `${url.backendURL}stakeholder/${id}`,
          config
        );

        if (response.data?.response?.data) {
          const data = response.data.response.data;
          setStakeholderData({
            name: data.name || "",
            address: data.address || "",
            contacts:
              data.contacts && data.contacts.length > 0
                ? data.contacts.map((contact) => ({
                    id: contact.id,
                    name: contact.name || "",
                    phone_number: contact.phone_number || "",
                    title: contact.title || "",
                    email: contact.email || "",
                  }))
                : [
                    {
                      name: "",
                      phone_number: "",
                      title: "",
                      email: "",
                    },
                  ],
          });
        }
      } catch (err) {
        handleError(err, "fetch-data");
      } finally {
        setLoading(false);
      }
    };

    fetchStakeholderData();
  }, [id, token]);

  const togglePopover = (popoverId) => {
    setShowPopover(showPopover === popoverId ? null : popoverId);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (showPopover !== null) {
        setShowPopover(null);
      }
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [showPopover]);

  const handleDeleteContact = async (
    contactId,
    contactName,
    index,
    remove,
    setFieldValue,
    values
  ) => {
    setDeleteLoading(true);
    try {
      const config = {
        headers: { Authorization: `${token}` },
      };

      await axios.delete(
        `${url.backendURL}stakeholder/${id}/contact/${contactId}`,
        config
      );

      remove(index);
      setStakeholderData((prevData) => ({
        ...prevData,
        contacts: prevData.contacts.filter((_, i) => i !== index),
      }));
      setAlert({
        message: `Contact "${contactName}" deleted successfully`,
        type: "success",
        show: true,
        alertFor: "contact-delete",
      });
      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      setShowPopover(null);

      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 3000);
    } catch (err) {
      handleError(err, "contact-delete");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      const config = {
        headers: { Authorization: `${token}` },
      };

      const stakeholderData = {
        name: values.name,
        address: values.address,
        ...(values.contacts &&
          values.contacts.length > 0 && { contacts: values.contacts }),
      };

      await axios.put(
        `${url.backendURL}stakeholder/${id}`,
        stakeholderData,
        config
      );

      setErrors({});
      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      setAlert({
        message: "Stakeholder and contacts updated successfully",
        type: "success",
        show: true,
        alertFor: "combined-update",
      });

      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
        navigate(`/dashboard/app/stakeholder-list`);
      }, 2000);
    } catch (err) {
      handleError(err, "combined-update");
    } finally {
      setSubmitting(false);
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");
      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const normalizePhoneNumber = (phone) => {
    if (!phone) return "";
    return phone.replace(/\D/g, "");
  };

  const normalizeEmail = (email) => {
    if (!email) return "";
    return email.toLowerCase().trim();
  };

  const createValidationSchema = () => {
    return Yup.object().shape({
      name: Yup.string()
        .matches(
          /^(?=.*[a-zA-Z])[a-zA-Z0-9 '&.-]*$/,
          "Stakeholder name can only contain letters and numbers"
        )
        .min(3, "Name must be at least 3 characters long")
        .max(255, "Name must not exceed 255 characters")
        .trim()
        .required("Name is required"),

      address: Yup.string()
        .min(5, "Address must be at least 5 characters long")
        .max(500, "Address must be less than or equal to 500 characters")
        .nullable(),

      contacts: Yup.array()
        .of(
          Yup.object().shape(
            {
              name: Yup.string().when(["phone_number", "title", "email"], {
                is: (phone_number, title, email) =>
                  (phone_number && phone_number.trim()) ||
                  (title && title.trim()) ||
                  (email && email.trim()),
                then: (schema) =>
                  schema
                    .min(2, "Contact name must be at least 2 characters long")
                    .max(255, "Contact name must not exceed 255 characters")
                    .trim()
                    .matches(
                      /^[\p{L}\p{M}.'\- ]+$/u,
                      "Contact name must start with a letter and can include spaces"
                    )
                    .required("Contact name is required"),
                otherwise: (schema) => schema.nullable(),
              }),

              phone_number: Yup.string().when(["name", "title", "email"], {
                is: (name, title, email) =>
                  (name && name.trim()) ||
                  (title && title.trim()) ||
                  (email && email.trim()),
                then: (schema) =>
                  schema
                    .trim()
                    .matches(
                      /^[0-9+\-]{10,15}$/,
                      "Phone number must be 10 to 15 digits"
                    )
                    .required("Phone number is required")
                    .test(
                      "unique-phone",
                      "This phone number is already used by another contact",
                      function (value) {
                        if (!value) return true;

                        const allContacts =
                          this.from?.[1]?.value?.contacts || [];
                        const currentIndex = parseInt(
                          this.path.match(/\[(\d+)\]/)?.[1] || 0
                        );
                        const normalizedCurrentPhone =
                          normalizePhoneNumber(value);

                        if (!normalizedCurrentPhone) return true;

                        const hasDuplicate = allContacts.some(
                          (contact, index) => {
                            if (index === currentIndex) return false;
                            const normalizedPhone = normalizePhoneNumber(
                              contact.phone_number
                            );
                            return (
                              normalizedPhone === normalizedCurrentPhone &&
                              normalizedPhone !== ""
                            );
                          }
                        );

                        return !hasDuplicate;
                      }
                    ),
                otherwise: (schema) => schema.nullable(),
              }),

              title: Yup.string()
                .min(2, "Title must be at least 2 characters long")
                .max(25, "Title must not exceed 25 characters")
                .trim()
                .nullable(),

              email: Yup.string()
                .trim()
                .email("Enter a valid email address")
                .test(
                  "no-spaces",
                  "Email may not contain any spaces at the beginning or end",
                  (value) => !value || value.trim() === value
                )
                .test(
                  "min-domain-segments",
                  "Enter a valid email address",
                  (value) => {
                    if (!value || value.trim() === "") return true;
                    const parts = value.split("@");
                    if (parts.length !== 2) return false;
                    const domainSegments = parts[1].split(".");
                    return (
                      domainSegments.length >= 2 &&
                      domainSegments.every((segment) => segment.length >= 2)
                    );
                  }
                )
                .test(
                  "realistic-email",
                  "Enter a valid email address",
                  (value) => {
                    if (!value || value.trim() === "") return true;
                    const [local, domain] = value.split("@");
                    if (!local || !domain) return false;

                    if (/^\d+$/.test(local)) return false;

                    if (local.length > 64) return false;

                    if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain))
                      return false;

                    return true;
                  }
                )
                .test(
                  "unique-email",
                  "This email address is already used by another contact",
                  function (value) {
                    if (!value || value.trim() === "") return true;

                    const allContacts = this.from?.[1]?.value?.contacts || [];
                    const currentIndex = parseInt(
                      this.path.match(/\[(\d+)\]/)?.[1] || 0
                    );
                    const normalizedCurrentEmail = normalizeEmail(value);

                    if (!normalizedCurrentEmail) return true;

                    const hasDuplicate = allContacts.some((contact, index) => {
                      if (index === currentIndex) return false;
                      const normalizedContactEmail = normalizeEmail(
                        contact.email
                      );
                      return (
                        normalizedContactEmail === normalizedCurrentEmail &&
                        normalizedContactEmail !== ""
                      );
                    });

                    return !hasDuplicate;
                  }
                )
                .nullable(),
            },
            [
              ["name", "phone_number"],
              ["name", "title"],
              ["name", "email"],
              ["phone_number", "title"],
              ["phone_number", "email"],
              ["title", "email"],
            ]
          )
        )
        .max(10, "Maximum 10 contacts allowed")
        .nullable(),
    });
  };

  const handleReset = (resetForm) => {
    resetForm();
    setErrors({});
    setResetKey((prev) => prev + 1);
  };

  if (loading) {
    return (
      <Row>
        <Col xl="12" lg="12">
          <Card>
            <Card.Body className="text-center">
              <FadeLoaderComponent />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  if (!stakeholderData) {
    return (
      <Row>
        <Col xl="12" lg="12">
          <Card>
            <Card.Body className="text-center">
              <div>Stakeholder not found</div>
              <Button
                variant="btn btn-primary"
                onClick={() => navigate("/dashboard/app/stakeholder-list")}
                className="mt-3"
              >
                Back to List
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Formik
        initialValues={stakeholderData}
        validationSchema={createValidationSchema()}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        validate={(values) => {
          const errors = {};

          if (values.contacts && values.contacts.length > 1) {
            const phoneNumbers = [];
            const emails = [];
            const contactErrors = [];

            values.contacts.forEach((contact, index) => {
              const contactError = {};
              if (contact.phone_number) {
                const normalizedPhone = normalizePhoneNumber(
                  contact.phone_number
                );
                if (normalizedPhone && phoneNumbers.includes(normalizedPhone)) {
                  contactError.phone_number =
                    "This phone number is already used by another contact";
                } else if (normalizedPhone) {
                  phoneNumbers.push(normalizedPhone);
                }
              }

              if (contact.email) {
                const normalizedEmail = normalizeEmail(contact.email);
                if (normalizedEmail && emails.includes(normalizedEmail)) {
                  contactError.email =
                    "This email address is already used by another contact";
                } else if (normalizedEmail) {
                  emails.push(normalizedEmail);
                }
              }

              if (Object.keys(contactError).length > 0) {
                contactErrors[index] = contactError;
              }
            });

            if (contactErrors.length > 0) {
              errors.contacts = contactErrors;
            }
          }

          return errors;
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          resetForm,
          setFieldValue,
          errors: formikErrors,
          touched,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <FocusError />
              <Row>
                <Col xl="12" lg="12" ref={alertRef}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between">
                      <div
                        className="breadcrumb header-title"
                        style={{ cursor: "pointer" }}
                      >
                        <h4
                          onClick={() =>
                            navigate("/dashboard/app/stakeholder-list")
                          }
                          className="text-primary card-title"
                          style={{ cursor: "pointer" }}
                        >
                          <span>Stakeholder</span>
                        </h4>
                        <h4 className="mx-2 card-title">{">"}</h4>
                        <h4 className="card-title">Edit</h4>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <AlertNotificationSimple
                        type={alert.type}
                        message={alert.message}
                        show={alert.show}
                        onClose={() => setAlert({ ...alert, show: false })}
                      />

                      <div className="stakeholder-info mb-4">
                        <h5 className="text-primary mb-3">
                          Stakeholder Information
                        </h5>
                        <Row>
                          <Col sm={6}>
                            <InputField
                              className="form-group"
                              type="text"
                              name="name"
                              placeholder="Enter stakeholder name"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              asterick={true}
                              disabled={
                                !stakeholderHasUpdatePermission &&
                                decodedToken?.roles?.some(
                                  (role) =>
                                    role.role_type !== roleTypes.SUPER_ADMIN
                                )
                              }
                              errorMessage={errors?.name}
                              label="Stakeholder Name"
                            />
                          </Col>
                        </Row>
                        <InputField
                          className="form-group w-full"
                          type="text"
                          as="textarea"
                          name="address"
                          placeholder="Enter address"
                          value={values.address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          asterick={false}
                          disabled={
                            !stakeholderHasUpdatePermission &&
                            decodedToken?.roles?.some(
                              (role) => role.role_type !== roleTypes.SUPER_ADMIN
                            )
                          }
                          errorMessage={errors?.address}
                          label="Address"
                        />
                      </div>

                      <hr className="my-4" />

                      <div className="contacts-info">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="text-primary mb-0">
                            Contact Information (Optional)
                          </h5>
                        </div>
                        {touched.contacts &&
                          formikErrors.contacts &&
                          typeof formikErrors.contacts === "string" && (
                            <Row className="mb-3">
                              <Col md={12}>
                                <div className="text-danger small">
                                  {formikErrors.contacts}
                                </div>
                              </Col>
                            </Row>
                          )}
                        {errors?.contacts && (
                          <Row className="mb-3">
                            <Col md={12}>
                              <div className="text-danger small">
                                {Array.isArray(errors.contacts)
                                  ? errors.contacts.join(", ")
                                  : errors.contacts}
                              </div>
                            </Col>
                          </Row>
                        )}
                        <FieldArray name="contacts">
                          {({ push, remove }) => (
                            <>
                              {values.contacts.map((contact, index) => (
                                <div
                                  key={index}
                                  className="contact-item mb-4 p-3"
                                  style={{
                                    border: "1px solid #e9ecef",
                                    borderRadius: "8px",
                                  }}
                                >
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">
                                      Contact {index + 1}
                                    </h6>
                                    <div className="d-flex gap-2">
                                      {contact.id &&
                                      typeof contact.id === "number" &&
                                      (stakeholderHasDeletePermission ||
                                        decodedToken?.roles?.some(
                                          (role) =>
                                            role.role_type ===
                                            roleTypes.SUPER_ADMIN
                                        )) &&
                                      contact.id > 0 ? (
                                        <OverlayTrigger
                                          trigger="click"
                                          show={
                                            showPopover ===
                                            `remove-contact-${index}`
                                          }
                                          onToggle={() =>
                                            togglePopover(
                                              `remove-contact-${index}`
                                            )
                                          }
                                          placement="top"
                                          overlay={
                                            <Popover
                                              id={`popover-remove-contact-${index}`}
                                            >
                                              <Popover.Header as="h3">
                                                <strong>Delete Contact</strong>{" "}
                                                <span>
                                                  ({contact.name || "Unnamed"})
                                                </span>
                                                ?
                                              </Popover.Header>
                                              <Popover.Body>
                                                Once deleted, this action cannot
                                                be undone.
                                                <div className="d-flex justify-content-end mt-2">
                                                  <Button
                                                    size="sm"
                                                    variant="danger"
                                                    className="me-2"
                                                    disabled={deleteLoading}
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      if (!deleteLoading) {
                                                        handleDeleteContact(
                                                          contact.id,
                                                          contact.name ||
                                                            "Unnamed",
                                                          index,
                                                          remove,
                                                          setFieldValue,
                                                          values
                                                        );
                                                      }
                                                    }}
                                                  >
                                                    {deleteLoading
                                                      ? "Deleting..."
                                                      : "Delete"}
                                                  </Button>
                                                  <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      togglePopover(
                                                        `remove-contact-${index}`
                                                      );
                                                    }}
                                                  >
                                                    Cancel
                                                  </Button>
                                                </div>
                                              </Popover.Body>
                                            </Popover>
                                          }
                                        >
                                          <Button
                                            type="button"
                                            variant="btn btn-outline-danger btn-sm"
                                            onClick={(e) => {
                                              e.preventDefault();
                                            }}
                                          >
                                            Remove Contact
                                          </Button>
                                        </OverlayTrigger>
                                      ) : (
                                        !contact.id && (
                                          <Button
                                            type="button"
                                            variant="btn btn-outline-danger btn-sm"
                                            onClick={() => remove(index)}
                                          >
                                            Remove Contact
                                          </Button>
                                        )
                                      )}
                                    </div>
                                  </div>

                                  <Row>
                                    <InputField
                                      className="col-md-6 form-group"
                                      type="text"
                                      name={`contacts[${index}].title`}
                                      placeholder="Enter title"
                                      value={contact.title}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      disabled={
                                        !stakeholderHasUpdatePermission &&
                                        decodedToken?.roles?.some(
                                          (role) =>
                                            role.role_type !==
                                            roleTypes.SUPER_ADMIN
                                        )
                                      }
                                      asterick={false}
                                      errorMessage={
                                        errors?.contacts?.[index]?.title
                                      }
                                      label="Title"
                                    />
                                    <InputField
                                      className="col-md-6 form-group"
                                      type="text"
                                      name={`contacts[${index}].name`}
                                      placeholder="Enter contact name"
                                      value={contact.name}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      disabled={
                                        !stakeholderHasUpdatePermission &&
                                        decodedToken?.roles?.some(
                                          (role) =>
                                            role.role_type !==
                                            roleTypes.SUPER_ADMIN
                                        )
                                      }
                                      asterick={false}
                                      errorMessage={
                                        errors?.contacts?.[index]?.name
                                      }
                                      label="Contact Name"
                                    />
                                  </Row>

                                  <Row>
                                    <Form.Group className="col-md-6 form-group">
                                      <Form.Label
                                        htmlFor={`contacts[${index}].phone_number`}
                                      >
                                        Phone Number
                                      </Form.Label>
                                      <CustomPhoneInput
                                        id={`contacts[${index}].phone_number`}
                                        name={`contacts[${index}].phone_number`}
                                        value={contact.phone_number}
                                        isDisabled={
                                          !stakeholderHasUpdatePermission &&
                                          decodedToken?.roles?.some(
                                            (role) =>
                                              role.role_type !==
                                              roleTypes.SUPER_ADMIN
                                          )
                                        }
                                        onChange={(value) =>
                                          setFieldValue(
                                            `contacts[${index}].phone_number`,
                                            value
                                          )
                                        }
                                        onBlur={handleBlur}
                                        placeholder="Enter phone number"
                                        country="us"
                                        isValid={(value, country) => {
                                          if (value.match(/12345/)) {
                                            return `Invalid value: ${value}, ${country.name}`;
                                          } else if (value.match(/1234/)) {
                                            return false;
                                          } else {
                                            return true;
                                          }
                                        }}
                                      />

                                      {((touched.contacts?.[index]
                                        ?.phone_number &&
                                        formikErrors.contacts?.[index]
                                          ?.phone_number) ||
                                        errors?.contacts?.[index]
                                          ?.phone_number) && (
                                        <div
                                          className="text-danger mt-1"
                                          style={{ fontSize: "0.875em" }}
                                        >
                                          {formikErrors.contacts?.[index]
                                            ?.phone_number ||
                                            errors?.contacts?.[index]
                                              ?.phone_number}
                                        </div>
                                      )}
                                    </Form.Group>

                                    <InputField
                                      className="col-md-6 form-group"
                                      type="email"
                                      name={`contacts[${index}].email`}
                                      placeholder="Enter email"
                                      value={contact.email}
                                      disabled={
                                        !stakeholderHasUpdatePermission &&
                                        decodedToken?.roles?.some(
                                          (role) =>
                                            role.role_type !==
                                            roleTypes.SUPER_ADMIN
                                        )
                                      }
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      asterick={false}
                                      errorMessage={
                                        errors?.contacts?.[index]?.email
                                      }
                                      label="Email"
                                    />
                                  </Row>
                                </div>
                              ))}

                              <div className="mb-4">
                                <Button
                                  type="button"
                                  variant="btn btn-primary"
                                  onClick={() =>
                                    push({
                                      id: null,
                                      name: "",
                                      phone_number: "",
                                      title: "",
                                      email: "",
                                    })
                                  }
                                  disabled={
                                    values.contacts.length >= 10 ||
                                    (!stakeholderHasUpdatePermission &&
                                      decodedToken?.roles?.some(
                                        (role) =>
                                          role.role_type !==
                                          roleTypes.SUPER_ADMIN
                                      ))
                                  }
                                >
                                  + Add New Contact
                                </Button>
                              </div>
                            </>
                          )}
                        </FieldArray>
                      </div>

                      <hr />
                      <div className="d-flex flex-wrap flex-md-nowrap justify-content-end gap-1 gap-md-2">
                        <Button
                          type="button"
                          className="me-2 me-md-5 px-3 px-md-4"
                          variant="btn btn-light"
                          onClick={() => handleReset(resetForm)}
                        >
                          Reset
                        </Button>

                        <Button
                          type="submit"
                          disabled={
                            isSubmitting ||
                            (!stakeholderHasUpdatePermission &&
                              decodedToken?.roles?.some(
                                (role) =>
                                  role.role_type !== roleTypes.SUPER_ADMIN
                              ))
                          }
                          variant="btn btn-primary"
                          className="me-2 me-md-5 px-3 px-md-4"
                        >
                          {isSubmitting ? "Updating..." : "Update"}
                        </Button>

                        <Button
                          type="button"
                          className="px-3 px-md-4"
                          variant="btn btn-danger"
                          onClick={() =>
                            navigate("/dashboard/app/stakeholder-list")
                          }
                        >
                          Cancel
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default StakeholderEdit;
